import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { WorkAbstract } from 'js/academia_components/cards/work_card/work_abstract';
import { WorkActions } from 'js/academia_components/cards/work_card/work_actions';
import { WorkCoauthors } from 'js/academia_components/cards/work_card/work_coauthors';
import { WorkContainer } from 'js/academia_components/cards/work_card/work_container';
import { WorkMentionsSelector } from 'js/academia_components/cards/work_card/work_mentions_selector';
import {
  WorkSnippet,
  UserCitationSnippet,
} from 'js/academia_components/cards/work_card/work_snippet';
import {
  WorkStats,
  WorkStatsContainer,
} from 'js/academia_components/cards/work_card/work_stats';
import { WorkTitle } from 'js/academia_components/cards/work_card/work_title';
import ImpressionWatcher from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import { Action, Filter, FILTER_TO_API_FORMAT } from 'js/mentions/common';

interface Work {
  downloadable_attachments?: { download_url: string }[];
  title: string;
  url: string;
  ordered_authors?: { display_name: string; important?: boolean }[];
  summary?: string;
  id: number;
}

export interface Props
  extends Partial<
      Record<
        | 'citation'
        | 'is_hidden'
        | 'is_public'
        | 'is_offsite'
        | 'wellKnownAuthor'
        | 'disableTitleLink'
        | 'disableAuthorLink'
        | 'highlyCited'
        | 'publicMentions',
        boolean
      >
    >,
    Partial<
      Record<'certificationCount' | 'recentViewCount' | 'position', number>
    > {
  mentioning_work_json?: Work;
  sectionTitle?: string;
  highlight?: string;
  cited_works?: Work[];
  onAction?: (action: Action) => void;
  id: number;
  freebieMention?: boolean;
  isFeaturedCitationContext?: boolean;
  isBlurred?: boolean;
  currentFilter?: Filter;
  ordering_method?:
    | 'confidence'
    | 'descending_id'
    | 'vector_ranking'
    | 'llm_score';
  mention_type?: string;
  confidence_score?: number | null;
  vector_similarity_score?: number | null;
}

export const MentionCard: React.FC<Props> = ({ onAction, ...props }) => {
  const {
    citation,
    highlyCited,
    is_hidden,
    is_public,
    is_offsite,
    highlight,
    cited_works,
    id,
    sectionTitle,
    mentioning_work_json,
    certificationCount,
    recentViewCount,
    wellKnownAuthor,
    publicMentions,
    disableTitleLink,
    disableAuthorLink,
    freebieMention,
    isFeaturedCitationContext,
    isBlurred,
    currentFilter = 'all',
    position,
    ordering_method,
    mention_type,
    confidence_score,
    vector_similarity_score,
  } = props;

  // overrides needed for whatever reason
  const mentioning = {
    ...mentioning_work_json,
    downloadUrl:
      !!mentioning_work_json &&
      !!mentioning_work_json.downloadable_attachments &&
      !!mentioning_work_json.downloadable_attachments.length &&
      !!mentioning_work_json.downloadable_attachments[0].download_url &&
      mentioning_work_json.downloadable_attachments[0].download_url,
  };

  const handleSelect =
    onAction &&
    (({ action }: { action: Action['action'] }) =>
      onAction({
        action,
        id,
        citation: !!citation,
      }));

  const HighlyCited = () => (
    <div className="wp-card-floatingBanner">
      {/* @ts-expect-error - bootstrap has not been updated to support react 18 which requires explicit children props */}
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id=";">
            Has 15 or more citations, or is one of the top 25% most-cited papers
            in its journal
          </Tooltip>
        }
      >
        <div className="wp-workCard-highly_cited">
          <span>HIGHLY CITED</span>
        </div>
      </OverlayTrigger>
    </div>
  );

  const normalizedFilter = FILTER_TO_API_FORMAT[currentFilter];
  return (
    <ImpressionWatcher
      entity_id={id}
      entity_type={ImpressionEntity.Mention}
      source="mentions.mention_card"
      extra={{
        filter: normalizedFilter,
        view_type: 'default',
        position,
        ordering_method,
        mention_type,
        confidence_score,
        vector_similarity_score,
      }}
    >
      <div>
        <div className="wp-card__spacer">
          <div className="wp-card__container">
            {highlyCited && <HighlyCited />}
          </div>
        </div>
        <WorkContainer tall={!highlight} isBlurred={isBlurred}>
          <WorkTitle>
            {!mentioning.url || disableTitleLink || isBlurred ? (
              mentioning.title
            ) : (
              <a target="_blank" href={mentioning.url}>
                {mentioning.title}
              </a>
            )}
          </WorkTitle>

          <WorkCoauthors
            linkKey={disableAuthorLink || isBlurred || 'profile_url'}
            textKey="display_name"
            list={mentioning.ordered_authors ?? []}
            freebieMention={freebieMention}
          />

          {mentioning.summary ? (
            <WorkAbstract lessButton freebieMention={freebieMention}>
              {mentioning.summary}
            </WorkAbstract>
          ) : (
            <div
              style={{
                width: '100%',
                height: 0,
                marginBottom: '.5em',
              }}
            />
          )}

          {!!mentioning.downloadUrl && (
            // @ts-expect-error ts-migrate(2741) FIXME: Property 'children' is missing in type '{ download... Remove this comment to see the full error message
            <WorkActions downloadUrl={mentioning.download_url} />
          )}

          {is_offsite && (
            <WorkStatsContainer>
              This mention was found in a paper hosted outside of Academia.edu
            </WorkStatsContainer>
          )}

          {highlight === '' && // I'm sorry Peter I couldn't figure out how to get this in the block below
            !!cited_works?.length && (
              <div style={{ flexBasis: '100%', marginBottom: '10px' }}>
                Cited:
              </div>
            )}

          {highlight === '' &&
            !!cited_works?.length &&
            cited_works.map((work) => (
              <UserCitationSnippet
                key={work.id}
                title={work.title}
                url={isBlurred ? undefined : work.url}
              />
            ))}

          {(sectionTitle ||
            recentViewCount ||
            certificationCount ||
            wellKnownAuthor) && (
            // @ts-expect-error ts-migrate(2741) FIXME: Property 'children' is missing in type '{ sectionT... Remove this comment to see the full error message
            <WorkStats
              sectionTitle={sectionTitle}
              recentlyRead={recentViewCount}
              certified={certificationCount}
              wellKnownAuthor={wellKnownAuthor}
            />
          )}

          <WorkSnippet htmlString={highlight} />

          {!publicMentions && !isFeaturedCitationContext && !freebieMention && (
            <WorkMentionsSelector
              // @ts-expect-error ts-migrate(2322) FIXME: Type '{ onSelect: ({ action }: { action: any; }) =... Remove this comment to see the full error message
              onSelect={handleSelect}
              isMe={is_public}
              notMe={is_hidden}
              isBlurred={isBlurred}
            />
          )}
        </WorkContainer>
      </div>
    </ImpressionWatcher>
  );
};
